package com.elan.edubridge.ui.shell

/**
 * `/guide` — 공개 이용 가이드. 학원장이 첫 운영을 시작할 때 보는 다섯 단계와, 학생·보호자가 앱으로
 * 학원에 연결되는 세 단계를 한 페이지에 둔다.
 *
 * 앱이 둘로 나뉜 뒤(APP_SPLIT_PLAN_v1.0.0)의 흐름이 정본이다: 학원 개설·플랜 선택·결제는 관리자 앱
 * ([MANAGER_APP_NAME]), 학생·보호자의 QR 스캔·알림은 학생·보호자 앱([STUDENT_APP_NAME]), PC 는 웹 콘솔.
 * 요금제 표는 관리자 앱의 플랜 선택 화면(`billing_plan_*` 문자열)과 같은 값만 적는다.
 *
 * 스크린샷은 실제 화면이다 — 웹 콘솔은 테스트 학원(새싹수학학원)에 로그인해 캡처했고, 앱 화면은 스토어
 * 스크린샷과 같은 실기기 캡처를 `tools/store-assets/make_web_images.py` 로 합성했다.
 */
object GuidePage {
    fun render() {
        renderPage("eb-guide", html())
    }

    private fun html(): String = """
        <header class="eb-landing-nav">
            <a class="eb-landing-nav__brand" href="/">${icon("icon-bridge-logo", "lg")}<span>EduBridge</span></a>
            <nav class="eb-landing-nav__links" aria-label="주 메뉴">
                <a href="/">서비스 소개</a><a href="/guide" aria-current="page">이용 가이드</a>
            </nav>
            <div class="eb-landing-nav__actions">
                <a class="eb-button eb-button--ghost" href="/login">로그인</a>
                <a class="eb-button eb-button--primary" href="/signup">회원가입</a>
            </div>
        </header>

        <main class="eb-guide__main">
            <section class="eb-guide__hero" aria-labelledby="guide-title">
                <span class="eb-guide__eyebrow">이용 가이드</span>
                <h1 id="guide-title">EduBridge, 이렇게 시작하세요</h1>
                <p>원장님은 관리자 앱이나 PC 웹 콘솔에서 학원을 열고, 학생·보호자는 EduBridge 앱으로 연결해요.
                개설부터 첫 등원 기록까지 다섯 단계면 충분해요.</p>
                <nav class="eb-guide__jump" aria-label="가이드 목차">
                    <a href="#roles">누가 어떤 앱을 쓰나요</a><a href="#owner">학원 운영 시작하기</a><a href="#member">학생·보호자 안내</a>
                </nav>
            </section>

            <section class="eb-guide__section" id="roles" aria-labelledby="roles-title">
                <h2 id="roles-title">누가 어떤 앱을 쓰나요?</h2>
                <p class="eb-guide__lead">두 앱은 같은 학원 데이터를 쓰고, 같은 계정으로 PC 웹에서도 로그인할 수 있어요.
                웹은 로그인하면 역할에 맞는 화면을 바로 열어요 — 원장·선생님은 학원 콘솔, 학생·보호자는 내 페이지.</p>
                <div class="eb-guide-roles">
                    <article class="eb-guide-role">
                        <h3>${icon("icon-academy", "md")}원장·선생님</h3>
                        <p><strong>${managerAppLink()}</strong> 앱(Android)과 PC <strong>웹 콘솔</strong>을 써요.
                        출결 처리와 QR 키오스크, 학생·반 관리, 초대와 가입 승인, 공지·과제, 통계가 여기 있어요.</p>
                        <p class="eb-meta">학원 개설과 구독 결제는 관리자 앱에서만 할 수 있어요.</p>
                    </article>
                    <article class="eb-guide-role">
                        <h3>${icon("icon-person", "md")}학생</h3>
                        <p><strong>${studentAppLink()}</strong> 앱을 써요. 등원·하원 QR 스캔, 공지와 게시판,
                        선생님이 낸 과제와 자료, 내 출결 통계와 수업 일정을 봐요.</p>
                        <p class="eb-meta">QR 스캔과 푸시 알림은 앱 전용이에요.</p>
                    </article>
                    <article class="eb-guide-role">
                        <h3>${icon("icon-parent-link", "md")}보호자</h3>
                        <p>학생과 같은 <strong>EduBridge</strong> 앱을 써요. 초대를 수락한 자녀의 등원·하원 알림을
                        그 순간 받고, 학원 공지와 게시판을 함께 봐요.</p>
                        <p class="eb-meta">보호자는 학원 권한이 없고, 연결은 언제든 해제할 수 있어요.</p>
                    </article>
                </div>
            </section>

            <section class="eb-guide__section" id="owner" aria-labelledby="owner-title">
                <h2 id="owner-title">학원 운영 시작하기</h2>
                <p class="eb-guide__lead">원장님 기준이에요. 1단계는 관리자 앱에서, 2단계부터는 관리자 앱과 웹 콘솔 어느 쪽에서든 할 수 있어요.</p>
                <ol class="eb-guide__steps" aria-label="학원 운영 시작 단계">
                    <li class="eb-guide-step">
                        <div class="eb-guide-step__copy">
                            <span class="eb-guide-step__number" aria-hidden="true">1</span>
                            <div>
                                <h3>학원 개설</h3>
                                <p>회원가입 후 <strong>$MANAGER_APP_NAME</strong> 앱에서 <strong>학원 개설하기</strong>를 선택하세요.
                                신청과 플랜 선택, Google Play 구독까지 앱 안에서 끝나고, 결제가 확인되는 즉시 학원이 활성화돼요.</p>
                                <ol class="eb-guide-step__details">
                                    <li>이메일 또는 Google 계정으로 회원가입해요(만 14세 이상).</li>
                                    <li>관리자 앱에서 학원명, 대표자명, 연락처, 주소와 소개를 입력해 신청해요.</li>
                                    <li>베이직·프로 중 플랜을 고르고 Google Play에서 구독을 시작해요. 무료체험 대상 계정은 첫 7일이 무료예요.</li>
                                    <li>PC에서는 <strong>내 페이지 › 학원 연결</strong>에서 신청서를 먼저 써 둘 수 있어요. 플랜 선택과 결제는 관리자 앱에서 이어져요.</li>
                                </ol>
                                ${managerAppBadges()}
                            </div>
                        </div>
                        <figure class="eb-guide-step__screen eb-guide-plans">
                            <table class="eb-guide-plans__table">
                                <caption>요금제 — 부가세 포함, 매월 자동 갱신</caption>
                                <thead><tr><th scope="col"><span class="eb-visually-hidden">항목</span></th><th scope="col">베이직</th><th scope="col">프로</th></tr></thead>
                                <tbody>
                                    <tr><th scope="row">월 요금</th><td><strong>₩29,000</strong></td><td><strong>₩49,000</strong></td></tr>
                                    <tr><th scope="row">학생</th><td>100명</td><td>무제한</td></tr>
                                    <tr><th scope="row">선생님</th><td>3명</td><td>무제한</td></tr>
                                    <tr><th scope="row">통계 기간</th><td>최근 3개월</td><td>전체 기간</td></tr>
                                    <tr><th scope="row">내보내기·감사 로그</th><td>—</td><td>상시 내보내기, 감사 로그 열람</td></tr>
                                </tbody>
                            </table>
                            <figcaption>출결 QR과 기록, 보호자 알림, 공지·게시판, 과제, 초대는 두 플랜이 같아요. 적용 조건과 최종 금액은 Google Play 결제 화면 기준이에요.</figcaption>
                        </figure>
                    </li>
                    <li class="eb-guide-step">
                        <div class="eb-guide-step__copy">
                            <span class="eb-guide-step__number" aria-hidden="true">2</span>
                            <div>
                                <h3>반 만들기와 학생 등록</h3>
                                <p>관리자 앱 홈의 <strong>반</strong>·<strong>학생</strong>, 또는 웹 콘솔의 <strong>학생</strong> 메뉴에서 시작하세요.
                                반에 요일과 시간을 넣어 두면 오늘 수업과 시간표가 채워져요.</p>
                                <ol class="eb-guide-step__details">
                                    <li>반 이름, 과목, 수업 요일과 시작·종료 시간을 정해 반을 만들어요.</li>
                                    <li><strong>학생 추가</strong>에서 이름, 학년, 학생 연락처, 보호자 이름과 연락처를 입력하고 반에 배정해요.</li>
                                    <li>같이 일하는 선생님은 홈의 <strong>선생님 초대</strong>로 8자리 코드를 발급해 관리자 앱으로 참여시켜요.</li>
                                </ol>
                            </div>
                        </div>
                        <figure class="eb-guide-step__screen">
                            <img src="/images/guide/students.png?v=2" alt="웹 콘솔 학생 메뉴 — 학생 목록과 학생 추가, 초대 버튼이 표시된 화면">
                            <figcaption>웹 콘솔 › 학생</figcaption>
                        </figure>
                    </li>
                    <li class="eb-guide-step">
                        <div class="eb-guide-step__copy">
                            <span class="eb-guide-step__number" aria-hidden="true">3</span>
                            <div>
                                <h3>학생·보호자 초대</h3>
                                <p>학생 행의 <strong>초대</strong>로 학생 또는 보호자에게 전달할 초대를 발급하세요.
                                받은 사람이 EduBridge 앱에서 수락하면 그때부터 출결과 알림이 연결돼요.</p>
                                <ol class="eb-guide-step__details">
                                    <li>초대 대상(학생·보호자)과 유효 기간을 고르고 <strong>초대 코드 발급</strong>을 눌러요.</li>
                                    <li>초대 링크, QR 코드, 전화로 불러 줄 8자리 코드 중 편한 방법으로 전달해요. 코드는 화면을 닫으면 다시 볼 수 없어요.</li>
                                    <li>학생·보호자가 ${studentAppLink()} 앱에서 링크를 열거나 코드를 입력해 수락해요. 보호자는 수락해야만 자녀 출결을 볼 수 있어요.</li>
                                    <li>학생이 먼저 찾아오게 하려면 <strong>설정</strong>의 학원 참여 코드를 알려 주세요. 신청이 들어오면 <strong>가입 신청</strong>에서 승인해요.</li>
                                </ol>
                            </div>
                        </div>
                        <figure class="eb-guide-step__screen">
                            <img src="/images/guide/invitation.png?v=2" alt="웹 콘솔에서 학생 초대 대상과 유효 기간을 고르는 화면">
                            <figcaption>웹 콘솔 › 학생 › 초대</figcaption>
                        </figure>
                    </li>
                    <li class="eb-guide-step">
                        <div class="eb-guide-step__copy">
                            <span class="eb-guide-step__number" aria-hidden="true">4</span>
                            <div>
                                <h3>등원·하원 기록 시작</h3>
                                <p>관리자 앱의 <strong>QR 키오스크</strong>나 웹 콘솔의 <strong>출결 QR</strong>을 태블릿에 띄워 두세요.
                                학생이 EduBridge 앱으로 스캔하는 순간 등원·하원이 기록되고 보호자에게 푸시 알림이 가요.</p>
                                <ol class="eb-guide-step__details">
                                    <li>QR을 띄울 위치 이름(예: 로비)을 정해 세션을 만들고 키오스크를 시작해요. QR은 주기적으로 자동 갱신되는 일회용이에요.</li>
                                    <li>학생이 스캔하면 홈의 실시간 출결 피드와 오늘 등원·하원·미등원 숫자가 바로 바뀌어요.</li>
                                    <li>QR 없이도 관리자 앱 <strong>출결</strong> 탭에서 반별 명단을 골라 출석·지각·결석을 한 번에 저장하고, 잘못된 기록은 사유를 남겨 정정해요.</li>
                                    <li>원장님과 선생님도 관리자 앱으로 출결 알림을 받아요. 키오스크로 쓰는 기기에는 알림이 가지 않아요.</li>
                                </ol>
                            </div>
                        </div>
                        <figure class="eb-guide-step__screen">
                            <img src="/images/feature-attendance-v1.png?v=1" alt="관리자 앱의 출결 QR 키오스크 화면과, 등원이 기록된 학생 앱 홈 화면">
                            <figcaption>관리자 앱 › QR 키오스크 · 학생 앱 › 홈</figcaption>
                        </figure>
                    </li>
                    <li class="eb-guide-step">
                        <div class="eb-guide-step__copy">
                            <span class="eb-guide-step__number" aria-hidden="true">5</span>
                            <div>
                                <h3>공지, 과제, 통계</h3>
                                <p>운영이 자리 잡으면 소통 기능을 써 보세요. 공지는 발행하는 순간 학원 구성원 전체에게 알림이 가고,
                                과제에 붙인 자료는 학생 앱에서 바로 열려요.</p>
                                <ol class="eb-guide-step__details">
                                    <li><strong>공지</strong>는 굵게·제목·목록·링크 서식으로 쓰고, 발행 후에도 수정·삭제할 수 있어요.</li>
                                    <li><strong>과제</strong>는 반 또는 학원 전체를 대상으로 마감일과 함께 내고, PDF·사진 자료를 붙여요. 학생 홈에는 마감이 가까운 과제부터 보여요.</li>
                                    <li><strong>통계</strong>에서 기간을 골라 반별·학생별 출석률을 확인해요. 베이직 플랜은 최근 3개월까지 볼 수 있어요.</li>
                                    <li><strong>게시판</strong>의 글과 댓글은 같은 학원 구성원에게만 보이고, 신고가 들어오면 <strong>신고 관리</strong>에서 처리해요.</li>
                                </ol>
                            </div>
                        </div>
                        <figure class="eb-guide-step__screen">
                            <img src="/images/guide/notice-editor.png?v=2" alt="웹 콘솔에서 제목·목록 서식을 넣어 공지를 작성하는 화면">
                            <figcaption>웹 콘솔 › 공지 › 공지 작성</figcaption>
                        </figure>
                    </li>
                </ol>
            </section>

            <section class="eb-guide__section" id="member" aria-labelledby="member-title">
                <h2 id="member-title">학생·보호자는 이렇게</h2>
                <p class="eb-guide__lead">학원에서 초대를 받았다면 세 단계면 돼요.</p>
                <ol class="eb-guide-mini" aria-label="학생·보호자 시작 단계">
                    <li>
                        <h3>EduBridge 앱 설치</h3>
                        <p>Google Play에서 <strong>$STUDENT_APP_NAME</strong>을 설치하고, 이메일 또는 Google 계정으로 가입해요. 광고는 없어요.</p>
                        ${studentAppBadges()}
                    </li>
                    <li>
                        <h3>학원과 연결</h3>
                        <p>학원에서 받은 초대 링크를 열거나 8자리 초대 코드를 입력해요. 보호자는 자녀 초대를 수락해야 출결을 볼 수 있어요.
                        학원 참여 코드를 받았다면 앱이나 웹의 <strong>학원 연결</strong>에서 가입을 신청하고 승인을 기다려요.</p>
                    </li>
                    <li>
                        <h3>등원·하원은 QR 스캔으로</h3>
                        <p>학원에 있는 QR 키오스크를 앱으로 스캔하면 등원·하원이 기록되고, 그 순간 보호자에게 알림이 가요.
                        홈에서 오늘 상태와 다음 수업, 읽지 않은 공지와 과제를 확인해요.</p>
                    </li>
                </ol>
                <p class="eb-meta">같은 계정으로 PC 웹(<a href="/login">로그인</a>)에서도 출결·공지·과제와 알림함을 볼 수 있어요. QR 스캔과 푸시 알림은 앱에서만 돼요.</p>
            </section>

            <section class="eb-guide__cta" aria-label="회원가입 시작">
                <h2>준비가 되셨나요?</h2>
                <p>회원가입 후 관리자 앱에서 학원을 열어 보세요. 무료체험 대상 계정은 첫 7일이 무료예요.</p>
                <a class="eb-button eb-button--primary eb-button--lg" href="/signup">회원가입하고 시작하기</a>
                ${managerAppBadges()}
            </section>
        </main>

        ${publicFooter()}
    """.trimIndent()
}
